<script setup>
import { reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useAlert } from 'dashboard/composables';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import CrmTaskAPI from 'dashboard/api/crmTasks';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';

const { t } = useI18n();
const store = useStore();
const agents = useMapGetter('agents/getAgents');

const dialogRef = ref(null);
const deal = ref(null);
const tasks = ref([]);
const isLoadingList = ref(false);
const isSaving = ref(false);
const completingId = ref(null);
const deletingId = ref(null);

const form = reactive({
  title: '',
  kind: 'task',
  dueAt: '',
  assigneeId: '',
});

// The jbuilder (`_crm_task.json.jbuilder`) renders snake_case, matching the
// rest of the CRM API — this is the same manual camelize step
// `dealsStore`/`CrmReport` already do by hand for their own responses,
// applied here since tasks have no Pinia store of their own (per the
// brief: dialogs/pages call `CrmTaskAPI` directly).
const camelizeTask = raw => ({
  id: raw.id,
  title: raw.title,
  kind: raw.kind,
  dueAt: raw.due_at,
  status: raw.status,
  completedAt: raw.completed_at,
  dealId: raw.deal_id,
  contactId: raw.contact_id,
  assigneeId: raw.assignee_id,
  assignee: raw.assignee,
  contact: raw.contact,
  deal: raw.deal,
});

const assigneeOptions = () => [
  { value: '', label: t('CRM_FUNIL.CARD.UNASSIGNED') },
  ...(agents.value || []).map(agent => ({
    value: agent.id,
    label: agent.name,
  })),
];

// Same kind list as `CrmAgenda` — duplicated for the same reason as
// `camelizeTask` above (no shared composable for this small CRM slice yet).
const kindOptions = () => [
  { value: 'task', label: t('CRM_FUNIL.KIND.TASK') },
  { value: 'meeting', label: t('CRM_FUNIL.KIND.MEETING') },
  { value: 'call', label: t('CRM_FUNIL.KIND.CALL') },
  { value: 'other', label: t('CRM_FUNIL.KIND.OTHER') },
];
const KIND_LABEL_KEYS = {
  task: 'TASK',
  meeting: 'MEETING',
  call: 'CALL',
  other: 'OTHER',
};
const kindLabel = kind =>
  t(`CRM_FUNIL.KIND.${KIND_LABEL_KEYS[kind] || 'TASK'}`);

const resetForm = () => {
  form.title = '';
  form.kind = 'task';
  form.dueAt = '';
  form.assigneeId = '';
};

const isOverdueTask = task =>
  task.status === 'pending' &&
  !!task.dueAt &&
  new Date(task.dueAt) < new Date();

// Same-local-day comparison as the rest of the CRM screens (CrmCard's
// `followUpStatus`, CrmReport's `toISO`): comparing `toLocaleDateString`
// strings avoids the browser's local timezone shifting "today" by a day.
const isTodayTask = task => {
  if (!task.dueAt || isOverdueTask(task)) return false;
  const dueDay = new Date(task.dueAt).toLocaleDateString('en-CA');
  const today = new Date().toLocaleDateString('en-CA');
  return dueDay === today;
};

const taskHighlightClass = task => {
  if (isOverdueTask(task)) return 'text-n-ruby-9';
  if (isTodayTask(task)) return 'text-n-amber-11';
  return 'text-n-slate-11';
};

const formattedDueAt = task =>
  task.dueAt ? new Date(task.dueAt).toLocaleString() : '';

const loadList = async () => {
  isLoadingList.value = true;
  try {
    const { data } = await CrmTaskAPI.get({ deal_id: deal.value.id });
    tasks.value = (data?.payload || []).map(camelizeTask);
  } catch {
    tasks.value = [];
  } finally {
    isLoadingList.value = false;
  }
};

const open = openedDeal => {
  deal.value = openedDeal;
  resetForm();
  dialogRef.value?.open();
  if (!agents.value?.length) store.dispatch('agents/get');
  loadList();
};

const handleAdd = async () => {
  if (isSaving.value || !form.title.trim()) return;

  isSaving.value = true;
  try {
    await CrmTaskAPI.create({
      title: form.title.trim(),
      kind: form.kind,
      due_at: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
      deal_id: deal.value.id,
      assignee_id: form.assigneeId || undefined,
    });
    resetForm();
    await loadList();
  } catch {
    useAlert(t('CRM_FUNIL.TASKS.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const handleComplete = async task => {
  if (completingId.value) return;

  completingId.value = task.id;
  try {
    await CrmTaskAPI.update(task.id, { status: 'done' });
    await loadList();
  } catch {
    useAlert(t('CRM_FUNIL.TASKS.ERROR'));
  } finally {
    completingId.value = null;
  }
};

const handleDelete = async task => {
  if (deletingId.value) return;

  deletingId.value = task.id;
  try {
    await CrmTaskAPI.delete(task.id);
    await loadList();
  } catch {
    useAlert(t('CRM_FUNIL.TASKS.ERROR'));
  } finally {
    deletingId.value = null;
  }
};

defineExpose({
  open,
  form,
  tasks,
  handleAdd,
  handleComplete,
  handleDelete,
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    :show-confirm-button="false"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('CRM_FUNIL.TASKS.TITLE') }}
      </span>

      <div class="flex flex-col gap-2">
        <p
          v-if="!isLoadingList && !tasks.length"
          class="text-xs text-n-slate-10"
        >
          {{ t('CRM_FUNIL.TASKS.NONE') }}
        </p>
        <div
          v-for="task in tasks"
          :key="task.id"
          class="flex items-start justify-between gap-2 p-2 border rounded-lg border-n-weak"
        >
          <div class="flex items-start gap-2 min-w-0">
            <Checkbox
              :model-value="task.status === 'done'"
              :disabled="task.status === 'done' || completingId === task.id"
              :aria-label="t('CRM_FUNIL.TASKS.COMPLETE')"
              @change="handleComplete(task)"
            />
            <div class="flex flex-col min-w-0 gap-0.5">
              <div class="flex items-center gap-2">
                <span
                  class="px-2 py-0.5 text-xs rounded-full whitespace-nowrap bg-n-slate-3 text-n-slate-11"
                >
                  {{ kindLabel(task.kind) }}
                </span>
                <span
                  class="text-xs truncate"
                  :class="
                    task.status === 'done'
                      ? 'text-n-slate-10 line-through'
                      : 'text-n-slate-12'
                  "
                >
                  {{ task.title }}
                </span>
              </div>
              <div
                v-if="task.dueAt"
                class="flex items-center gap-1 text-xs"
                :class="taskHighlightClass(task)"
              >
                <span>{{ formattedDueAt(task) }}</span>
                <span v-if="isOverdueTask(task)">
                  · {{ t('CRM_FUNIL.TASKS.OVERDUE') }}
                </span>
                <span v-else-if="isTodayTask(task)">
                  · {{ t('CRM_FUNIL.TASKS.TODAY') }}
                </span>
              </div>
              <span
                v-if="task.assignee?.name"
                class="text-xs text-n-slate-10 truncate"
              >
                {{ task.assignee.name }}
              </span>
            </div>
          </div>
          <Button
            icon="i-lucide-trash-2"
            variant="ghost"
            color="ruby"
            size="sm"
            type="button"
            :aria-label="t('CRM_FUNIL.CARD.DELETE')"
            :disabled="deletingId === task.id"
            @click="handleDelete(task)"
          />
        </div>
      </div>

      <div class="flex flex-col gap-2 pt-2 border-t border-n-weak">
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CRM_FUNIL.KIND.LABEL') }}
          </label>
          <Select v-model="form.kind" :options="kindOptions()" />
        </div>
        <Input
          v-model="form.title"
          :label="t('CRM_FUNIL.TASKS.TITLE_FIELD')"
          :disabled="isSaving"
        />
        <Input
          v-model="form.dueAt"
          type="datetime-local"
          :label="t('CRM_FUNIL.TASKS.DUE')"
          :disabled="isSaving"
        />
        <div class="flex flex-col gap-1">
          <label class="mb-0.5 text-heading-3 text-n-slate-12">
            {{ t('CRM_FUNIL.TASKS.ASSIGNEE') }}
          </label>
          <Select v-model="form.assigneeId" :options="assigneeOptions()" />
        </div>
        <Button
          :label="t('CRM_FUNIL.TASKS.ADD')"
          type="button"
          size="sm"
          :is-loading="isSaving"
          :disabled="isSaving || !form.title.trim()"
          @click="handleAdd"
        />
      </div>
    </div>
  </Dialog>
</template>
